import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import { IconChevron, SocialMediaIcons } from 'gatorcomponents';
import {
  format, subDays, addDays, differenceInDays, parseISO,
} from 'date-fns';
import SnappyAiLogo from '@/pages/marketing/AdsHandler/media/snappy-ai.svg';
import useLocale from '@/hooks/useLocale';
import { DateInput } from './DateInput';
import * as Styles from './Header.styles';

const MAX_DATE_RANGE_DAYS = 30;
const DEFAULT_DATE_FROM = 30;

const Header = ({
  selectedAccount,
  connectedAccounts = [],
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onAccountChange,
}) => {
  const { marketing: marketingLocale } = useLocale();
  const { multiAgentScreens: multiAgentScreensLocale } = marketingLocale;
  const { header: headerLocale } = multiAgentScreensLocale;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const defaultDateFrom = useMemo(() => format(subDays(new Date(), DEFAULT_DATE_FROM), 'yyyy-MM-dd'), []);

  useEffect(() => {
    if (!dateFrom && onDateFromChange) {
      onDateFromChange(defaultDateFrom);
    }
    if (!dateTo && onDateToChange) {
      onDateToChange(today);
    }
  }, [dateFrom, dateTo, onDateFromChange, onDateToChange, today, defaultDateFrom]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleAccountSelect = (account) => {
    if (onAccountChange) {
      onAccountChange(account);
    }
    setIsDropdownOpen(false);
  };

  const handleDateFromChangeWithValidation = (newDate) => {
    if (!newDate || newDate > today) {
      return;
    }

    const currentDateTo = dateTo || today;
    const daysDifference = differenceInDays(parseISO(currentDateTo), parseISO(newDate));

    if (daysDifference <= MAX_DATE_RANGE_DAYS && onDateFromChange) {
      onDateFromChange(newDate);
    }
  };

  const handleDateToChangeWithValidation = (newDate) => {
    if (!newDate || newDate > today) {
      return;
    }

    const currentDateFrom = dateFrom || defaultDateFrom;
    const daysDifference = differenceInDays(parseISO(newDate), parseISO(currentDateFrom));

    if (daysDifference <= MAX_DATE_RANGE_DAYS && onDateToChange) {
      onDateToChange(newDate);
    }
  };

  const currentAccount = selectedAccount || { name: '', type: 'instagram' };
  const currentConnectedAccounts = connectedAccounts || [];

  const dateFromMin = useMemo(() => {
    const currentDateTo = dateTo || today;
    return format(subDays(parseISO(currentDateTo), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
  }, [dateTo, today]);

  const dateFromMax = useMemo(() => {
    const currentDateTo = dateTo || today;
    return currentDateTo <= today ? currentDateTo : today;
  }, [dateTo, today]);

  const dateToMin = useMemo(() => {
    const currentDateFrom = dateFrom || defaultDateFrom;
    return currentDateFrom;
  }, [dateFrom, defaultDateFrom]);

  const dateToMax = useMemo(() => {
    const currentDateFrom = dateFrom || defaultDateFrom;
    const maxAllowedDate = format(addDays(parseISO(currentDateFrom), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
    return maxAllowedDate <= today ? maxAllowedDate : today;
  }, [dateFrom, defaultDateFrom, today]);

  return (
    <Styles.Header>
      <Styles.HeaderBrand>
        <Styles.HeaderLogo src={SnappyAiLogo} alt="Snappy AI" />
        <Styles.HeaderTitleWrapper>
          <Styles.HeaderMainTitle>
            {headerLocale.brandTitle}
          </Styles.HeaderMainTitle>
          <Styles.HeaderSubtitle>
            {headerLocale.brandSubtitle}
          </Styles.HeaderSubtitle>
        </Styles.HeaderTitleWrapper>
      </Styles.HeaderBrand>

      <Styles.InputsWrapper>
        <Styles.AccountDropdownWrapper ref={dropdownRef}>
          <Styles.AccountDropdownButton
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            $isOpen={isDropdownOpen}
          >
            <Styles.AccountIconWrapper>
              <SocialMediaIcons icon={currentAccount.type} brandColors size="md" />
            </Styles.AccountIconWrapper>
            <Styles.AccountInfo>
              <Styles.AccountLabel>{headerLocale.accountLabel}</Styles.AccountLabel>
              <Styles.AccountName>{currentAccount.name}</Styles.AccountName>
            </Styles.AccountInfo>
            <IconChevron
              direction={isDropdownOpen ? 'top' : 'bottom'}
              size="sm"
              color="primaryMedium"
            />
          </Styles.AccountDropdownButton>

          <Styles.AccountDropdownMenu $isOpen={isDropdownOpen}>

            <Styles.ConnectedAccountsSection>
              <Styles.ConnectedAccountsTitle>{headerLocale.connectedAccountsTitle}</Styles.ConnectedAccountsTitle>
              <Styles.ConnectedAccountsList>
                {currentConnectedAccounts.map(account => (
                  <Styles.ConnectedAccountCard
                    key={account.id}
                    type="button"
                    onClick={() => handleAccountSelect(account)}
                  >
                    <Styles.AccountIconWrapper>
                      <SocialMediaIcons icon={account.type} brandColors size="md" />
                    </Styles.AccountIconWrapper>
                    <Styles.ConnectedAccountInfo>
                      <Styles.ConnectedAccountName>{account.name}</Styles.ConnectedAccountName>
                      <Styles.ConnectedAccountDescription>
                        {account.description}
                      </Styles.ConnectedAccountDescription>
                    </Styles.ConnectedAccountInfo>
                  </Styles.ConnectedAccountCard>
                ))}
              </Styles.ConnectedAccountsList>
            </Styles.ConnectedAccountsSection>
          </Styles.AccountDropdownMenu>
        </Styles.AccountDropdownWrapper>

        <Styles.DateInputsWrapper>
          <DateInput
            label={headerLocale.dateFromLabel}
            value={dateFrom || defaultDateFrom}
            onChange={handleDateFromChangeWithValidation}
            min={dateFromMin}
            max={dateFromMax}
          />

          <Styles.DateInputsSeparator />

          <DateInput
            label={headerLocale.dateToLabel}
            value={dateTo || today}
            onChange={handleDateToChangeWithValidation}
            min={dateToMin}
            max={dateToMax}
          />
        </Styles.DateInputsWrapper>
      </Styles.InputsWrapper>
    </Styles.Header>
  );
};

export default Header;
